import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { adminService } from "../../services";
import { getWorkspaceOverviewCatalog } from "../../navigation/workspaceOverviewCatalog";
import "./ManageWorkspaceOverview.css";

const MAX_KEYS = 24;

const ManageWorkspaceOverview = () => {
  const catalog = useMemo(() => getWorkspaceOverviewCatalog(), []);
  const catalogById = useMemo(() => {
    const m = new Map();
    catalog.forEach((c) => m.set(c.id, c));
    return m;
  }, [catalog]);

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [navKeys, setNavKeys] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addKey, setAddKey] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingTenants(true);
        const data = await adminService.getAllTenants();
        if (!cancelled) setTenants(data || []);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.message || "Failed to load tenants");
        }
      } finally {
        if (!cancelled) setLoadingTenants(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setNavKeys([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingConfig(true);
        const data = await adminService.getTenantWorkspaceOverview(tenantId);
        const keys = Array.isArray(data?.navKeys) ? data.navKeys : [];
        if (!cancelled) setNavKeys(keys);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message || "Failed to load workspace overview",
          );
          setNavKeys([]);
        }
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const availableToAdd = useMemo(() => {
    const set = new Set(navKeys);
    return catalog.filter((c) => !set.has(c.id));
  }, [catalog, navKeys]);

  const addSelected = () => {
    if (!addKey || navKeys.length >= MAX_KEYS) return;
    if (navKeys.includes(addKey)) return;
    setNavKeys((prev) => [...prev, addKey]);
    setAddKey("");
  };

  const move = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= navKeys.length) return;
    setNavKeys((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const removeAt = (index) => {
    setNavKeys((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    try {
      setSaving(true);
      await adminService.updateTenantWorkspaceOverview(tenantId, {
        navKeys,
      });
      toast.success("Workspace Overview saved");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to save workspace overview",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!tenantId) return;
    if (
      !window.confirm(
        "Reset to app default Overview tiles for this tenant? This clears the custom list.",
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      await adminService.updateTenantWorkspaceOverview(tenantId, {
        navKeys: null,
      });
      setNavKeys([]);
      toast.success("Cleared — home will use default Overview tiles");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to clear workspace overview",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loadingTenants) {
    return (
      <div className="manage-ws-overview-loading">Loading tenants…</div>
    );
  }

  return (
    <div className="manage-ws-overview">
      <div className="manage-ws-overview-header">
        <h2>Workspace Overview</h2>
        <p>
          Choose which modules appear under “Overview” on the home page for
          each tenant. Order is preserved (up to {MAX_KEYS} tiles).
        </p>
      </div>

      <div className="manage-ws-overview-field">
        <label htmlFor="ws-tenant">Tenant</label>
        <select
          id="ws-tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        >
          <option value="">— Select tenant —</option>
          {tenants.map((t) => (
            <option key={t.tenantId} value={t.tenantId}>
              {t.tenantName || t.tenantCode || t.tenantId}
            </option>
          ))}
        </select>
      </div>

      {tenantId && (
        <>
          {loadingConfig ? (
            <p className="manage-ws-overview-loading">Loading configuration…</p>
          ) : (
            <>
              <div className="manage-ws-overview-add-row">
                <select
                  aria-label="Add module"
                  value={addKey}
                  onChange={(e) => setAddKey(e.target.value)}
                >
                  <option value="">— Add module —</option>
                  {availableToAdd.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} ({c.group})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-ws-primary"
                  onClick={addSelected}
                  disabled={!addKey || navKeys.length >= MAX_KEYS}
                >
                  Add
                </button>
              </div>

              {navKeys.length === 0 ? (
                <div className="manage-ws-overview-empty">
                  No custom tiles — the app default Overview list is used until
                  you add modules and save.
                </div>
              ) : (
                <ul className="manage-ws-overview-list">
                  {navKeys.map((id, index) => {
                    const meta = catalogById.get(id);
                    return (
                      <li key={`${id}-${index}`} className="manage-ws-overview-row">
                        <span className="manage-ws-overview-row-label">
                          {meta?.label || id}
                          {meta?.group && (
                            <span className="manage-ws-overview-row-meta">
                              {meta.group}
                            </span>
                          )}
                        </span>
                        <div className="manage-ws-overview-actions">
                          <button
                            type="button"
                            title="Move up"
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            title="Move down"
                            disabled={index === navKeys.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="btn-ws-danger"
                            onClick={() => removeAt(index)}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="manage-ws-overview-footer">
                <button
                  type="button"
                  className="btn-ws-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  className="btn-ws-secondary"
                  onClick={handleClear}
                  disabled={saving}
                >
                  Use default (clear custom)
                </button>
              </div>
            </>
          )}
        </>
      )}

      <p className="manage-ws-overview-hint">
        Users only see tiles they can access; hidden keys are skipped on the
        home page.
      </p>
    </div>
  );
};

export default ManageWorkspaceOverview;
